import React, { useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import PageHeader from './PageHeader';

import { API } from '../apiClient';
function api(path, options = {}) {
  const token = localStorage.getItem('token');
  return fetch(`${API}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers || {})
    }
  }).then(async r => {
    if (!r.ok) throw new Error((await r.json()).detail || 'API error');
    return r.json();
  });
}

function money(value) {
  return `${Number(value || 0).toFixed(2)} zł`;
}

function shortDate(dateStr) {
  if (!dateStr) return '—';
  try {
    return new Date(dateStr).toLocaleDateString('pl-PL', { day: 'numeric', month: 'short' });
  } catch {
    return dateStr;
  }
}

function dateTime(dateStr) {
  if (!dateStr) return '—';
  try {
    return new Date(dateStr).toLocaleString('pl-PL', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
  } catch {
    return dateStr;
  }
}

function toneClass(tone) {
  if (tone === 'success') return 'success';
  if (tone === 'danger') return 'danger';
  if (tone === 'warning') return 'warning';
  return 'info';
}

function apartmentShortStatus(apartment) {
  if (!apartment) return 'Ładowanie';
  if (apartment.state === 'occupied') return 'Gość';
  if (apartment.state === 'needs_cleaning') return 'Sprzątanie';
  return 'Gotowe';
}

export default function Dashboard() {
  const ctx = useOutletContext() || {};
  const navigate = useNavigate();
  const bookings = ctx.bookings || [];
  const expenses = ctx.expenses || [];
  const dashboard = ctx.dashboard;
  const reload = ctx.reload;
  const user = ctx.user || {};
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState('');

  const apartment = dashboard?.apartment_status;
  const nextCheckin = dashboard?.next_checkin_summary;
  const cleaning = dashboard?.maria_settlement;
  const nextPrep = dashboard?.next_guest_preparation;
  const mariaExpenses = dashboard?.maria_expenses_settlement;
  const nuki = dashboard?.nuki_status;
  const nukiBattery = nuki?.battery || {};
  const nukiBatteryPercent = Number.isFinite(Number(nukiBattery.percent)) ? Number(nukiBattery.percent) : null;
  const nukiBatteryLow = nukiBattery.available && (nukiBattery.critical || (nukiBatteryPercent !== null && nukiBatteryPercent < 20));
  const fallbackTodoMoney = expenses
    .filter(e => !e.reimbursed && (e.user_id === 3 || e.user_name === 'Maria'))
    .reduce((a, b) => a + Number(b.amount), 0);
  const totalDue = dashboard?.maria_total_due || {
    amount: (cleaning?.unpaid_total || 0) + (mariaExpenses?.unreimbursed_total ?? fallbackTodoMoney),
    status: ((cleaning?.unpaid_total || 0) + (mariaExpenses?.unreimbursed_total ?? fallbackTodoMoney)) > 0 ? 'unpaid' : 'paid'
  };
  const recentExpenses = mariaExpenses?.recent_unreimbursed_expenses || [];
  const canAdmin = user.role === 'admin';

  async function confirmCleaningPayment(jobId) {
    if (!jobId) return;
    setError('');
    setBusy(`cleaning-${jobId}`);
    try {
      await api(`/cleaning-jobs/${jobId}/payment`, {
        method: 'PATCH',
        body: JSON.stringify({ payment_status: 'paid' })
      });
      await reload?.();
    } catch (err) {
      setError(err.message || 'Nie udało się potwierdzić płatności');
    } finally {
      setBusy(null);
    }
  }

  function goToNextBooking() {
    const booking = nextCheckin?.booking;
    if (!booking) return;
    const ref = booking.external_id || booking.id;
    navigate(`/rezerwacje/${encodeURIComponent(ref)}`);
  }

  async function reimburseExpense(expenseId) {
    setError('');
    setBusy(`expense-${expenseId}`);
    try {
      await api(`/expenses/${expenseId}/reimburse`, {
        method: 'PATCH',
        body: JSON.stringify({ reimbursed: true })
      });
      await reload?.();
    } catch (err) {
      setError(err.message || 'Nie udało się oznaczyć zakupu jako zwrócony');
    } finally {
      setBusy(null);
    }
  }

  const cleaningStatus = cleaning?.status || 'none';
  const totalDueStatus = totalDue.status || (Number(totalDue.amount || 0) > 0 ? 'unpaid' : 'paid');

  return (
    <>
      <PageHeader
        icon="🏠"
        title="Dashboard"
        description="Tylko aktualne decyzje: mieszkanie, najbliższy przyjazd i rozliczenie z Marią."
        helpKey="dashboard"
        stats={[
          { label: 'Rezerwacje', value: dashboard?.reservation_count ?? bookings.length, tone: 'info' },
          { label: 'Najbliższy check-in', value: nextCheckin?.label || '—', tone: nextCheckin?.days_until === 0 ? 'warning' : 'info' },
          { label: 'Status', value: apartmentShortStatus(apartment), tone: toneClass(apartment?.tone) },
          { label: 'Nuki', value: nuki?.short_status || '—', tone: toneClass(nuki?.tone) },
          { label: 'Do zapłaty Marii', value: money(totalDue.amount), tone: totalDueStatus === 'unpaid' ? 'danger' : 'success' },
        ]}
      />

      {error && <div className="error dashboard-error">{error}</div>}

      {nukiBatteryLow && (
        <div className="error dashboard-error" role="alert" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <div>
            <strong>Uwaga: bateria Nuki jest niska</strong>
            <div style={{ marginTop: 4 }}>
              {nukiBatteryPercent !== null ? `${nukiBatteryPercent}%` : 'Brak procentu'}{nukiBattery.critical ? ' · krytyczna' : ''}
            </div>
          </div>
          <button type="button" className="secondary" onClick={() => navigate('/nuki')}>
            Otwórz Nuki
          </button>
        </div>
      )}

      <section className="dashboard-grid dashboard-grid-optimized">
        <article className={`card status-card hero-status ${toneClass(apartment?.tone)}`}>
          <div className="status-card-head">
            <span className={`status-chip ${toneClass(apartment?.tone)}`}>Status mieszkania</span>
          </div>
          <h2>{apartment?.label || 'Ładowanie statusu mieszkania...'}</h2>
          {apartment?.current_booking && (
            <p className="muted">Obecny gość: {apartment.current_booking.guest_name}</p>
          )}
          {apartment?.last_checkout_booking && !apartment?.current_booking && (
            <p className="muted">Ostatni checkout: {shortDate(apartment.last_checkout_booking.checkout)} · {apartment.last_checkout_booking.guest_name}</p>
          )}
          {apartment?.state === 'needs_cleaning' && apartment?.next_booking && (
            <p className="muted strong-muted">Sprzątanie możliwe tylko przed check-in: {shortDate(apartment.next_booking.checkin)}</p>
          )}
          {apartment?.readiness_event && (
            <div className={`whatsapp-readiness-box ${apartment.readiness_event.status === 'ready' ? 'success' : 'danger'}`}>
              <strong>{apartment.readiness_event.status === 'ready' ? 'Potwierdzone przez WhatsApp' : 'Problem z WhatsApp'}</strong>
              <span>{apartment.readiness_event.confirmed_by || 'WhatsApp'} · {shortDate(apartment.readiness_event.confirmed_at)}</span>
              {apartment.readiness_event.note && <small>„{apartment.readiness_event.note}”</small>}
            </div>
          )}
        </article>

        <article
          className={`card status-card info ${nextCheckin?.booking ? 'clickable-card' : ''}`}
          onClick={nextCheckin?.booking ? goToNextBooking : undefined}
          onKeyDown={(event) => {
            if (!nextCheckin?.booking) return;
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              goToNextBooking();
            }
          }}
          role={nextCheckin?.booking ? 'button' : undefined}
          tabIndex={nextCheckin?.booking ? 0 : undefined}
          aria-label={nextCheckin?.booking ? `Przejdź do rezerwacji ${nextCheckin.booking.guest_name}` : undefined}
        >
          <div className="status-card-head">
            <span className="status-chip info">Najbliższa rezerwacja</span>
            {nextCheckin?.booking && <span className="card-link-hint">Otwórz →</span>}
          </div>
          <h2>{nextCheckin?.label || 'Brak nadchodzącej rezerwacji'}</h2>
          {nextCheckin?.booking ? (
            <>
              <p className="muted">Check-in: {shortDate(nextCheckin.booking.checkin)}</p>
              <p className="muted">Gość: {nextCheckin.booking.guest_name}</p>
              {nextPrep?.guest_count > 0 && (
                <div className="maria-prep-box">
                  <strong>Dla Marii: {nextPrep.guest_count} osób</strong>
                  <span>{nextPrep.label}</span>
                </div>
              )}
            </>
          ) : (
            <p className="muted">Nie ma kolejnego przyjazdu w kalendarzu.</p>
          )}
        </article>

        <article className={`card status-card ${toneClass(nuki?.tone)}`}>
          <div className="status-card-head" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
            <span className={`status-chip ${toneClass(nuki?.tone)}`}>Status Nuki</span>
            {nukiBatteryLow && (
              <span className="status-chip danger" style={{ fontSize: 11, letterSpacing: '0.02em' }}>
                {nukiBattery.critical ? 'Krytyczna bateria' : nukiBatteryPercent !== null ? `${nukiBatteryPercent}% baterii` : 'Niska bateria'}
              </span>
            )}
          </div>
          <h2>{nuki?.label || 'Ładowanie statusu Nuki...'}</h2>
          <div className="settlement-breakdown">
            <p className="muted">Kody lokalne: {nuki?.code_stats?.total ?? 0} · planowane: {nuki?.code_stats?.planned ?? 0} · aktywne: {nuki?.code_stats?.active ?? 0}</p>
            {nuki?.latest_run && (
              <p className="muted">Ostatni przebieg: {dateTime(nuki.latest_run.finished_at || nuki.latest_run.started_at)} · {nuki.latest_run.status}</p>
            )}
            {nuki?.last_guest_2_event ? (
              <p className="muted strong-muted">Kod „Gość 2” ostatnio użyty: {dateTime(nuki.last_guest_2_event.event_date)}</p>
            ) : (
              <p className="muted strong-muted">Brak zapisanego użycia kodu „Gość 2” w ostatnich logach.</p>
            )}
            {nuki?.last_access_event && nuki.last_access_event.auth_name !== 'Gość 2' && (
              <p className="muted">Ostatni zidentyfikowany dostęp: {nuki.last_access_event.auth_name} · {dateTime(nuki.last_access_event.event_date)}</p>
            )}
          </div>
        </article>

        <article className={`card status-card ${totalDueStatus === 'paid' ? 'success' : 'danger'}`}>
          <div className="status-card-head">
            <span className={`status-chip ${totalDueStatus === 'paid' ? 'success' : 'danger'}`}>Rozliczenie z Marią</span>
          </div>
          <h2>{totalDue.label || (totalDueStatus === 'paid' ? 'Maria jest rozliczona' : `Do zapłaty: ${money(totalDue.amount)}`)}</h2>
          <div className="settlement-breakdown">
            <p className="muted">Sprzątanie: {money(totalDue.cleaning_amount ?? cleaning?.unpaid_total ?? 0)}</p>
            <p className="muted">Zakupy: {money(totalDue.expenses_amount ?? mariaExpenses?.unreimbursed_total ?? fallbackTodoMoney)}</p>
          </div>
          {cleaning?.last_done_cleaning_job_id && cleaning.status !== 'paid' && canAdmin && (
            <div className="settlement-actions">
              <button
                onClick={() => confirmCleaningPayment(cleaning.last_done_cleaning_job_id)}
                disabled={busy === `cleaning-${cleaning.last_done_cleaning_job_id}`}
              >
                {busy === `cleaning-${cleaning.last_done_cleaning_job_id}` ? 'Zapisuję...' : 'Potwierdź zapłatę za sprzątanie'}
              </button>
            </div>
          )}
        </article>

        {recentExpenses.length > 0 && (
          <article className="card status-card danger maria-expenses-detail">
            <div className="status-card-head">
              <span className="status-chip danger">Nierozliczone zakupy</span>
            </div>
            <div className="expense-mini-list">
              {recentExpenses.map(expense => (
                <div className="expense-mini-row" key={expense.id}>
                  <div>
                    <strong>{money(expense.amount)}</strong>
                    <span>{shortDate(expense.created_at)} · {expense.description}</span>
                    {expense.receipt_note && <small>{expense.receipt_note}</small>}
                  </div>
                  {canAdmin && (
                    <button
                      className="secondary small"
                      onClick={() => reimburseExpense(expense.id)}
                      disabled={busy === `expense-${expense.id}`}
                    >
                      {busy === `expense-${expense.id}` ? 'Zapisuję...' : 'Zwrócone'}
                    </button>
                  )}
                </div>
              ))}
            </div>
          </article>
        )}
      </section>
    </>
  );
}
